import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Grid,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const ACADEMIC_TYPES = [
  { value: 1, labelKey: "academic.student" },
  { value: 2, labelKey: "academic.intern" },
  { value: 3, labelKey: "academic.resident" },
];
const STATUS_OPTIONS = [
  { value: 1, labelKey: "academic.active" },
  { value: 2, labelKey: "academic.onLeave" },
  { value: 3, labelKey: "academic.graduated" },
  { value: 4, labelKey: "academic.withdrawn" },
];
const GENDER_OPTIONS = [
  { value: 1, labelKey: "academic.male" },
  { value: 2, labelKey: "academic.female" },
  { value: 3, labelKey: "academic.other" },
];

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AcademicFormDialog({ open, onClose, academic, onSave, saving }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    trainee_id: "",
    gender: "",
    date_of_birth: "",
    email: "",
    phone: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    academic_type: 1,
    status: 1,
    enrollment_date: "",
    expected_end_date: "",
    program: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const prevOpenRef = useRef(false);

  // Only initialize form when dialog opens (not on every academic reference change while open)
  // so typing in trainee_id / date_of_birth doesn't lose focus or reset the field
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (justOpened) {
      if (academic) {
        setForm({
          first_name: academic.first_name || "",
          last_name: academic.last_name || "",
          trainee_id: academic.trainee_id || "",
          gender: academic.gender ?? "",
          date_of_birth: academic.date_of_birth ? academic.date_of_birth.slice(0, 10) : "",
          email: academic.email || "",
          phone: academic.phone || "",
          emergency_contact_name: academic.emergency_contact_name || "",
          emergency_contact_phone: academic.emergency_contact_phone || "",
          academic_type: academic.academic_type ?? 1,
          status: academic.status ?? 1,
          enrollment_date: academic.enrollment_date ? academic.enrollment_date.slice(0, 10) : "",
          expected_end_date: academic.expected_end_date ? academic.expected_end_date.slice(0, 10) : "",
          program: academic.program || "",
          notes: academic.notes || "",
        });
      } else {
        setForm({
          first_name: "",
          last_name: "",
          trainee_id: "",
          gender: "",
          date_of_birth: "",
          email: "",
          phone: "",
          emergency_contact_name: "",
          emergency_contact_phone: "",
          academic_type: 1,
          status: 1,
          enrollment_date: "",
          expected_end_date: "",
          program: "",
          notes: "",
        });
      }
      setErrors({});
    }
  }, [open, academic]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const next = {};
    if (!form.first_name?.trim()) next.first_name = t("validation.required", "Required");
    if (form.email?.trim() && !emailRe.test(form.email)) next.email = t("validation.invalidEmail", "Invalid email");
    if (form.enrollment_date && form.expected_end_date && form.expected_end_date < form.enrollment_date) {
      next.expected_end_date = t("academic.expectedEndAfterEnrollment", "Expected end must be after enrollment");
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSave({
      ...form,
      trainee_id: form.trainee_id?.trim() || null,
      gender: form.gender === "" ? null : parseInt(form.gender, 10),
      date_of_birth: form.date_of_birth || null,
      enrollment_date: form.enrollment_date || null,
      expected_end_date: form.expected_end_date || null,
    });
  };

  const Section = ({ title, children }) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: "text.secondary" }}>
        {title}
      </Typography>
      <Grid container spacing={2}>
        {children}
      </Grid>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {academic ? t("academic.editProfile", "Edit Academic") : t("academic.addProfile", "Add Academic")}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Section title={t("academic.personal", "Personal")}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required name="first_name" label={t("academic.firstName", "First name")} value={form.first_name} onChange={handleChange} size="small" error={!!errors.first_name} helperText={errors.first_name} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth name="last_name" label={t("academic.lastName", "Last name")} value={form.last_name} onChange={handleChange} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth name="trainee_id" label={t("academic.traineeId", "Trainee ID (optional)")} value={form.trainee_id} onChange={handleChange} size="small" placeholder="e.g. TR-2026-001" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth name="gender" select label={t("academic.gender", "Gender")} value={form.gender} onChange={handleChange} size="small">
                <MenuItem value="">—</MenuItem>
                {GENDER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{t(opt.labelKey)}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth name="date_of_birth" type="date" label={t("academic.dateOfBirth", "Date of birth")} value={form.date_of_birth} onChange={handleChange} InputLabelProps={{ shrink: true }} size="small" />
            </Grid>
            <Grid item xs={12} sm={6} />
            <Grid item xs={12} sm={6}>
              <TextField fullWidth name="email" type="email" label={t("academic.email", "Email")} value={form.email} onChange={handleChange} size="small" error={!!errors.email} helperText={errors.email} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth name="phone" label={t("academic.phone", "Phone")} value={form.phone} onChange={handleChange} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth name="emergency_contact_name" label={t("academic.emergencyContactName", "Emergency contact name")} value={form.emergency_contact_name} onChange={handleChange} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth name="emergency_contact_phone" label={t("academic.emergencyContactPhone", "Emergency contact phone")} value={form.emergency_contact_phone} onChange={handleChange} size="small" />
            </Grid>
          </Section>

          <Section title={t("academic.training", "Training")}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth name="academic_type" select label={t("academic.type", "Type")} value={form.academic_type} onChange={handleChange} size="small">
                {ACADEMIC_TYPES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{t(opt.labelKey)}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth name="status" select label={t("academic.status", "Status")} value={form.status} onChange={handleChange} size="small">
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{t(opt.labelKey)}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth name="enrollment_date" type="date" label={t("academic.enrollmentDate", "Enrollment date")} value={form.enrollment_date} onChange={handleChange} InputLabelProps={{ shrink: true }} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth name="expected_end_date" type="date" label={t("academic.expectedEndDate", "Expected end date")} value={form.expected_end_date} onChange={handleChange} InputLabelProps={{ shrink: true }} size="small" error={!!errors.expected_end_date} helperText={errors.expected_end_date} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth name="program" label={t("academic.program", "Program")} value={form.program} onChange={handleChange} size="small" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth name="notes" label={t("academic.notes", "Notes")} value={form.notes} onChange={handleChange} multiline rows={2} size="small" />
            </Grid>
          </Section>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("common.cancel", "Cancel")}</Button>
          <Button type="submit" variant="contained" disabled={saving || !form.first_name?.trim()}>
            {saving ? <CircularProgress size={24} /> : t("common.save", "Save")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
